import React, { useContext, useState } from "react";
import { Alert, Descriptions, Spin, Tabs } from "antd";
import Axios from "axios";
import useRMateChart from "./useRMateChart";
import MapPropsContext from "../MapPropsContext";
import { getApiHost } from "../../../utils";
import {
  getChart1Data,
  getChart2Data,
  getChart3Data,
  getLayoutForChartTypeImage, // chart 1 layout
  getLayoutForChartTypeMultiSeriesStacked, // chart 2 layout
  getLayoutForChartTypeCombination3DColumnLine, // chart 3 layout
} from "./RMateChartFusionPopulationUtils";
import ChartPane from "./ChartPane";
import useDebounce from "../../../utils/hooks/useDebounce";
import imgPersonType1 from "../../../images/person_type_1.png";
import imgPersonType2 from "../../../images/person_type_2.png";
import imgPersonType3 from "../../../images/person_type_3.png";
import { StyledButton } from "../../../styled-components/Button";
import { TI_MODAL_RESULT_CLOSE_BUTTON } from "../../../Constants";

function RMateChartFusionPopulation({ disabled }) {
  const { chartAjaxParams } = useContext(MapPropsContext);

  const { sidoCode, searchYear, prm_check, prm_check2, gubun_code, chkRadSvcString } = chartAjaxParams;

  const [isInit, setIsInit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [prevParams, setPrevParams] = useState();
  const [parsedObject, setParsedObject] = useState({});

  const [densityTableData, setDensityTableData] = useState();

  useDebounce(
    () => {
      if (isInit) {
        const url = getApiHost() + "/rwdinfolabstats/getRdwAnalsStatsData3.json";
        const newParams = { sidoCode, gubun_code, searchYear, chkRadSvcString };

        if (JSON.stringify(prevParams) !== JSON.stringify(newParams)) {
          setPrevParams(newParams);
          setIsLoading(true);
          setParsedObject({}); // 초기화

          Axios.get(url, { params: newParams })
            .then(({ data }) => {
              const { chart1Data, chart1Data2, table1Html, table1Html2 } = getChart1Data(data, {
                prm_check,
                prm_check2,
                gubun_code,
              });
              const { chart2Data, chart2Data2 } = getChart2Data(data);
              const { chart3Data } = getChart3Data(data);

              setDensityTableData(data.item);

              setParsedObject({
                chart1Data,
                chart1Data2,
                table1Html,
                table1Html2,
                chart2Data,
                chart2Data2,
                chart3Data,
              });
            })
            .catch((e) => console.error(e))
            .finally(() => {
              setIsLoading(false);
            });
        }
      }
    },
    1000,
    [isInit, prevParams, chartAjaxParams, setIsLoading]
  );

  const { chart1Data, chart2Data, chart3Data } = parsedObject;

  const [tabState, setTabState] = useState(0);

  return useRMateChart({
    init: () => setIsInit(true),
    style: { minHeight: "540px" },
    disabled,
    children: (
      <div style={{ position: "relative", height: "100%" }}>
        {/*{isLoading && <Spin style={{position: 'absolute', left: '50%', top: '50%', zIndex: 1}} size={"large"}/>}*/}
        {isLoading && (
          <div
            style={{
              position: "absolute",
              top: 100,
              zIndex: 1,
              height: "90%",
              width: "100%",
              backgroundColor: "white",
            }}
          >
            <Spin size={"large"}>
              <Alert
                message="차트 로딩 중"
                description="선택한 무선국의 정보를 불러오는 중입니다.."
                type="info"
                showIcon
                style={{ height: "470px" }}
              />
            </Spin>
          </div>
        )}

        <div style={{ paddingBottom: "5px" }}>
          <StyledButton
            isActive={tabState === 0}
            onClick={() => setTabState(0)}
            style={{ borderRadius: "4px 4px 0px 0px", padding: "0px 4px" }}
            tabIndex={TI_MODAL_RESULT_CLOSE_BUTTON - 3}
          >
            인구유형별 무선국 밀도(인구 1명/무선국수) - ${searchYear}년
          </StyledButton>
          <StyledButton
            isActive={tabState === 1}
            onClick={() => setTabState(1)}
            style={{ borderRadius: "0px 0px 0px 0px", padding: "0px 4px" }}
            tabIndex={TI_MODAL_RESULT_CLOSE_BUTTON - 2}
          >
            인구유형별 지역별 무선국 밀도(무선국수/인구 천 명) - ${searchYear}년
          </StyledButton>
          <StyledButton
            isActive={tabState === 2}
            onClick={() => setTabState(2)}
            style={{ borderRadius: "0px 0px 4px 4px", padding: "0px 4px" }}
            tabIndex={TI_MODAL_RESULT_CLOSE_BUTTON - 1}
          >
            년도별 무선국 수 / 인구 수 추세 - ${searchYear}년
          </StyledButton>
        </div>
        {tabState === 0 && !isLoading && densityTableData && (
          <Descriptions
            bordered
            title={"무선국 밀도(무선국수/인구 천 명)"}
            size={"small"}
            column={4}
            layout={"vertical"}
            style={{ textAlign: "center" }}
          >
            {densityTableData &&
              densityTableData.map(({ DATA_FIELD, DATA_SUM2, DATA_TYPE }, index) => {
                if (DATA_TYPE === "DATA_TYPE2") {
                  return (
                    <Descriptions.Item
                      key={DATA_FIELD}
                      label={
                        <div key={index}>
                          {DATA_FIELD === "어린이" && <img src={imgPersonType2} alt="어린이" style={{ width: 15 }} />}
                          {DATA_FIELD === "경제인구" && (
                            <img src={imgPersonType1} alt="경제인구" style={{ width: 15 }} />
                          )}
                          {DATA_FIELD === "노년" && <img src={imgPersonType3} alt="노년인구" style={{ width: 15 }} />}
                          {"\u00A0"}
                          {DATA_FIELD}
                        </div>
                      }
                    >
                      {Math.floor(DATA_SUM2 * 100) / 100}
                    </Descriptions.Item>
                  );
                } else {
                  return null;
                }
              })}
          <ChartPane
            data={chart1Data}
            processor={() => chart1Data}
            layoutProcessor={() => getLayoutForChartTypeImage(CHART1_LAYOUT_PROPS, { searchYear })}
            style={{ height: 360 }}
          />
          </Descriptions>
        )}
        {tabState === 1 && (
          <ChartPane
            data={chart2Data}
            processor={() => chart2Data}
            layoutProcessor={() =>
              getLayoutForChartTypeMultiSeriesStacked(CHART2_LAYOUT_PROPS, {
                gubun_code,
                searchYear,
              })
            }
            style={{ height: 470 }}
          />
        )}
        {tabState === 2 && (
          <ChartPane
            data={chart3Data}
            processor={() => chart3Data}
            layoutProcessor={() => getLayoutForChartTypeCombination3DColumnLine(CHART3_LAYOUT_PROPS, { searchYear })}
            style={{ height: 470 }}
          />
        )}
      </div>
    ),
  });
}

const CHART1_LAYOUT_PROPS = {
  title: "인구유형별 무선국 밀도(인구 1명/무선국수)",
  xtitle: "",
  ytitle: "",
  chartType: "Image",
  layOutStyle: "Master",
  masterName: "무선국 밀도",
  valueFixed: 5,
};

const CHART2_LAYOUT_PROPS = {
  title: "인구유형별 지역별 무선국 밀도(무선국수/인구 천 명)",
  xtitle: "", //지역명
  ytitle: "무선국 밀도(무선국수/인구 천 명)",
  chartType: "Multi Series Stacked",
  layOutStyle: "Detail",
  masterName: "무선국 밀도",
  valueFixed: 5,
};

const CHART3_LAYOUT_PROPS = {
  title: "년도별 무선국 수 / 인구 수 추세",
  xtitle: "년도",
  ytitle: "인구유형별 인구수",
  ytitle2: "무선국 수",
  chartType: "Combination3DColumnLine",
  layOutStyle: "Detail",
  masterName: "무선국 밀도",
  valueFixed: 5,
};

export default RMateChartFusionPopulation;
